import { useMemo, useState } from 'react';
import React from 'react';

import { Modal, ModalActions } from '../../ui/Modal/Modal';
import { Select } from '../../ui/Select/Select';
import { HelpHint } from '../../ui/HelpHint/HelpHint';
import { EyeIcon, EyeOffIcon } from '../../../assets/icons/icons';
import { useGetAdminOrganizationsQuery } from '../../../entities/access/api/accessApi';
import type { AccessUser, UserRole } from '../../../types/access';
import { cx } from '../../../utils/cx';
import styles from './UserFormModal.module.scss';

export interface UserFormValues {
    fullName: string;
    email: string;
    password: string;
    role: UserRole;
    organizationId: string;
}

export interface UserFormSubmitOptions {
    sendEmail: boolean;
}

interface UserFormModalProps {
    user: AccessUser | null;
    isSaving: boolean;
    serverError?: string | null;
    onSubmit: (values: UserFormValues, options: UserFormSubmitOptions) => void;
    onClose: () => void;
}

const ROLE_OPTIONS: { value: UserRole; label: string }[] = [
    { value: 'user', label: 'Пользователь' },
    { value: 'admin', label: 'Администратор' },
    { value: 'superadmin', label: 'Суперадминистратор' },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_PASSWORD_LENGTH = 10;

const ORGANIZATIONS_PAGE_SIZE = 500;

function getPasswordError(password: string): string | null {
    if (password.length < MIN_PASSWORD_LENGTH) {
        return `Пароль должен содержать не менее ${MIN_PASSWORD_LENGTH} символов`;
    }
    if (/\s/.test(password)) {
        return 'Пароль не должен содержать пробелов';
    }
    if (!/[A-Z]/.test(password)) {
        return 'Добавьте хотя бы одну заглавную латинскую букву';
    }
    if (!/[a-z]/.test(password)) {
        return 'Добавьте хотя бы одну строчную латинскую букву';
    }
    if (!/\d/.test(password)) {
        return 'Добавьте хотя бы одну цифру';
    }
    return null;
}

type PendingAction = 'save' | 'send' | null;

export function UserFormModal({ user, isSaving, serverError, onSubmit, onClose }: UserFormModalProps) {
    const isEdit = user !== null;

    const firstPage = useGetAdminOrganizationsQuery({
        page_size: ORGANIZATIONS_PAGE_SIZE,
        page_num: 1,
    });

    const secondPage = useGetAdminOrganizationsQuery(
        { page_size: ORGANIZATIONS_PAGE_SIZE, page_num: 2 },
        { skip: (firstPage.data?.pages ?? 1) < 2 },
    );

    const isOrganizationsLoading = firstPage.isLoading || secondPage.isLoading;

    const organizationOptions = useMemo(
        () =>
            [...(firstPage.data?.data ?? []), ...(secondPage.data?.data ?? [])].map((item) => ({
                id: String(item.id),
                name: item.name?.trim() || 'Без названия',
                hint: item.inn ? String(item.inn) : undefined,
            })),
        [firstPage.data, secondPage.data],
    );

    const [values, setValues] = useState<UserFormValues>({
        fullName: user?.fullName ?? '',
        email: user?.email ?? '',
        password: '',
        role: user?.role ?? 'user',
        organizationId: user?.organizationId ?? '',
    });

    const [repeat, setRepeat] = useState('');

    const [passwordVisible, setPasswordVisible] = useState(false);
    const [repeatVisible, setRepeatVisible] = useState(false);

    const [touched, setTouched] = useState(false);
    const [pendingAction, setPendingAction] = useState<PendingAction>(null);

    function set<K extends keyof UserFormValues>(key: K, value: UserFormValues[K]): void {
        setValues((current) => ({ ...current, [key]: value }));
    }

    const passwordRequired = !isEdit || values.password !== '';
    const passwordMismatch = passwordRequired && repeat !== values.password;

    const repeatDisabled = isEdit && values.password === '';

    const errors = {
        fullName: values.fullName.trim() === '' ? 'Укажите ФИО' : null,
        email: EMAIL_PATTERN.test(values.email.trim()) ? null : 'Укажите корректный email',
        password: passwordRequired ? getPasswordError(values.password) : null,
        repeat: passwordMismatch ? 'Пароли не совпадают' : null,
        organizationId: !isEdit && values.organizationId === '' ? 'Выберите организацию' : null,
    };

    const isValid = Object.values(errors).every((error) => error === null);

    const canSendEmail = isEdit && values.password !== '' && isValid;

    function submit(sendEmail: boolean): void {
        setTouched(true);

        if (isValid && !isSaving) {
            setPendingAction(sendEmail ? 'send' : 'save');
            onSubmit(
                {
                    ...values,
                    fullName: values.fullName.trim(),
                    email: values.email.trim().toLowerCase(),
                },
                { sendEmail },
            );
        }
    }

    function error(key: keyof typeof errors): string | null {
        return touched ? errors[key] : null;
    }

    const isPending = (action: PendingAction): boolean => isSaving && pendingAction === action;

    return (
        <Modal
            open
            title={isEdit ? 'Редактирование пользователя' : 'Новый пользователь'}
            closeOnOverlayClick={false}
            onClose={onClose}
            footer={
                <ModalActions>
                    <button type="button" className={styles.secondary} disabled={isSaving} onClick={onClose}>
                        Отмена
                    </button>
                    <button
                        type="button"
                        className={styles.secondary}
                        disabled={isSaving}
                        aria-busy={isPending('save')}
                        aria-label={isPending('save') ? 'Сохраняем…' : undefined}
                        onClick={() => submit(false)}
                    >
                        {isPending('save') ? <span className={styles.spinner} aria-hidden /> : isEdit ? 'Сохранить' : 'Создать'}
                    </button>
                    {isEdit && (
                        <button
                            type="button"
                            className={cx(styles.primary, styles.send)}
                            disabled={isSaving || !canSendEmail}
                            aria-busy={isPending('send')}
                            aria-label={isPending('send') ? 'Отправляем…' : undefined}
                            onClick={() => submit(true)}
                        >
                            {isPending('send') ? <span className={styles.spinner} aria-hidden /> : 'Сохранить и отправить на почту'}
                        </button>
                    )}
                </ ModalActions>
            }
        >
            <div className={styles.form}>
                {serverError && <div className={styles.error}>{serverError}</div>}

                <label className={styles.field}>
                    <span className={styles.label}>ФИО</span>
                    <input
                        className={styles.input}
                        value={values.fullName}
                        placeholder="Иванов Иван Иванович"
                        onChange={(event) => set('fullName', event.target.value)}
                    />
                    {error('fullName') && <span className={styles.error}>{error('fullName')}</span>}
                </label>

                <label className={styles.field}>
                    <span className={styles.label}>Email</span>
                    <input
                        className={styles.input}
                        type="email"
                        autoComplete="off"
                        value={values.email}
                        placeholder="user@example.ru"
                        onChange={(event) => set('email', event.target.value)}
                    />
                    {error('email') && <span className={styles.error}>{error('email')}</span>}
                </label>

                <label className={styles.field}>
                    <span className={styles.label}>Пароль</span>

                    <span className={styles.passwordField}>
                        <input
                            className={cx(styles.input, styles.passwordInput)}
                            type={passwordVisible ? 'text' : 'password'}
                            autoComplete="new-password"
                            placeholder={isEdit ? 'Новый пароль' : `Не менее ${MIN_PASSWORD_LENGTH} символов`}
                            value={values.password}
                            onChange={(event) => set('password', event.target.value)}
                        />
                        <button
                            type="button"
                            className={styles.eye}
                            onClick={() => setPasswordVisible((visible) => !visible)}
                            aria-label={passwordVisible ? 'Скрыть пароль' : 'Показать пароль'}
                        >
                            {passwordVisible ? <EyeOffIcon /> : <EyeIcon />}
                        </button>
                    </span>

                    {error('password') ? (
                        <span className={styles.error}>{error('password')}</span>
                    ) : (
                        (!isEdit || values.password !== '') && (
                            <span className={styles.hint}>
                                Не менее {MIN_PASSWORD_LENGTH} символов: заглавные и строчные латинские
                                буквы, цифры, без пробелов
                            </span>
                        )
                    )}
                </label>

                <label className={styles.field}>
                    <span className={styles.label}>Повторите пароль</span>

                    <span className={styles.passwordField}>
                        <input
                            className={cx(styles.input, styles.passwordInput)}
                            type={repeatVisible ? 'text' : 'password'}
                            autoComplete="new-password"
                            placeholder={repeatDisabled ? 'Не требуется' : 'Повторите пароль'}
                            value={repeat}
                            disabled={repeatDisabled}
                            onChange={(event) => setRepeat(event.target.value)}
                        />
                        <button
                            type="button"
                            className={styles.eye}
                            onClick={() => setRepeatVisible((visible) => !visible)}
                            aria-label={repeatVisible ? 'Скрыть пароль' : 'Показать пароль'}
                        >
                            {repeatVisible ? <EyeOffIcon /> : <EyeIcon />}
                        </button>
                    </span>

                    {error('repeat') && <span className={styles.error}>{error('repeat')}</span>}
                </label>

                <div className={styles.field}>
                    <span className={styles.label}>Роль</span>
                    <Select<UserRole>
                        options={ROLE_OPTIONS}
                        value={values.role}
                        onChange={(role) => set('role', role)}
                        aria-label="Роль пользователя"
                    />
                </div>

                {!isEdit && (
                    <div className={styles.field}>
                        <span className={styles.label}>
                            Наименование организации
                            <HelpHint>
                                Если организации нет в списке, вы можете её создать через вкладку
                                «Организации»
                            </HelpHint>
                        </span>

                        <Select
                            options={organizationOptions.map((item) => ({
                                value: item.id,
                                label: item.name,
                                hint: item.hint,
                            }))}
                            value={values.organizationId}
                            loading={isOrganizationsLoading}
                            invalid={error('organizationId') !== null}
                            placeholder="Выберите организацию"
                            emptyText="Организация не найдена"
                            searchable
                            searchPlaceholder="Начните вводить название или ИНН"
                            onChange={(id) => set('organizationId', String(id))}
                            aria-label="Организация"
                        />

                        {error('organizationId') && (
                            <span className={styles.error}>{error('organizationId')}</span>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
}